import type { ReactNode } from 'react';
import { Badge } from '@/components/ui/badge';
import { cn } from '@/lib/utils';
import type { ProcurementType, RiskLevel, Segment } from '../data/types';

// Badge recipes from DESIGN.md: 50 background, 800 text, 300 border.
export type StatusTone = 'success' | 'warning' | 'danger' | 'neutral';

const TONE_CLASS: Record<StatusTone, string> = {
  success:
    'border-emerald-300 bg-emerald-50 text-emerald-800 dark:border-emerald-800 dark:bg-emerald-950 dark:text-emerald-200',
  warning:
    'border-amber-300 bg-amber-50 text-amber-800 dark:border-amber-800 dark:bg-amber-950 dark:text-amber-200',
  danger:
    'border-red-300 bg-red-50 text-red-800 dark:border-red-800 dark:bg-red-950 dark:text-red-200',
  neutral: 'border-border bg-muted text-muted-foreground',
};

const BADGE_BASE = 'px-2 py-0.5 text-[11px] leading-4';

interface StatusBadgeProps {
  tone: StatusTone;
  children: ReactNode;
  className?: string;
}

export function StatusBadge({ tone, children, className }: StatusBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={cn(BADGE_BASE, TONE_CLASS[tone], className)}
    >
      {children}
    </Badge>
  );
}

const RISK_LABEL: Record<RiskLevel, string> = {
  low: 'Low',
  medium: 'Medium',
  high: 'High',
  critical: 'Critical',
};

// Low risk stays neutral so color marks the exceptions only.
const RISK_CLASS: Record<RiskLevel, string> = {
  low: 'border-border bg-transparent text-muted-foreground',
  medium: TONE_CLASS.warning,
  high: TONE_CLASS.danger,
  critical: 'border-red-700 bg-red-600 text-white',
};

interface RiskBadgeProps {
  level: RiskLevel;
  /** 0-100 risk score, shown before the level when provided. */
  score?: number;
  className?: string;
}

export function RiskBadge({ level, score, className }: RiskBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={cn(BADGE_BASE, 'gap-1', RISK_CLASS[level], className)}
    >
      {score === undefined ? null : (
        <span className="tabular-nums">{score}</span>
      )}
      <span>{RISK_LABEL[level]}</span>
    </Badge>
  );
}

interface TypeBadgeProps {
  type: ProcurementType;
  className?: string;
}

export function TypeBadge({ type, className }: TypeBadgeProps) {
  if (type === 'direct') {
    return (
      <Badge variant="secondary" className={cn(BADGE_BASE, className)}>
        Direct
      </Badge>
    );
  }
  return (
    <Badge variant="outline" className={cn(BADGE_BASE, className)}>
      Indirect
    </Badge>
  );
}

const SEGMENT_CLASS: Record<Segment, string> = {
  Strategic: 'border-transparent bg-secondary text-foreground font-semibold',
  Preferred: 'text-foreground',
  Approved: 'text-muted-foreground',
  Onboarding: 'text-muted-foreground',
  Prospect: 'text-muted-foreground',
};

interface SegmentBadgeProps {
  segment: Segment;
  className?: string;
}

export function SegmentBadge({ segment, className }: SegmentBadgeProps) {
  return (
    <Badge
      variant="outline"
      className={cn(BADGE_BASE, SEGMENT_CLASS[segment], className)}
    >
      {segment}
    </Badge>
  );
}
